'use client'

import { useMemo, useState } from 'react'
import ProjectCard from '@/components/ProjectCard'
import { PROJECT_CATEGORIES, type Project, type ProjectCategory } from '@/lib/projects'

type Filter = 'All' | ProjectCategory

/**
 * /work page interactive surface — sticky left sidebar filter +
 * responsive masonry-ish grid on the right. The grid swaps instantly
 * as the filter changes.
 */
export default function WorkGrid({ projects }: { projects: Project[] }) {
  const [active, setActive] = useState<Filter>('All')

  // Pre-compute counts per category so the sidebar can show a dim "(n)"
  // next to each filter without scanning on every render. A project that
  // sits in multiple categories is counted once per category it belongs
  // to (so the totals across filters can sum to more than the raw
  // project count — that's intentional).
  const counts = useMemo(() => {
    const map: Record<string, number> = { All: projects.length }
    for (const c of PROJECT_CATEGORIES) {
      map[c] = projects.filter((p) => p.categories.includes(c)).length
    }
    return map
  }, [projects])

  const filtered = useMemo(
    () =>
      active === 'All'
        ? projects
        : projects.filter((p) => p.categories.includes(active as ProjectCategory)),
    [active, projects],
  )

  // Filter list, in display order. Categories with zero projects are
  // still rendered (greyed out + non-interactive) so the sidebar doesn't
  // change shape as you publish new work.
  const filters: Filter[] = ['All', ...PROJECT_CATEGORIES]

  return (
    <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-12 md:gap-16">
      {/* ───────────────── Sidebar filter ───────────────── */}
      <aside className="md:sticky md:top-28 md:self-start">
        <p className="font-display font-medium uppercase tracking-[0] text-xs text-charcoal-400 mb-5">
          [ Filter by ]
        </p>

        <ul className="flex md:flex-col flex-wrap md:flex-nowrap gap-1.5 md:gap-1 md:-ml-3">
          {filters.map((f) => {
            const count = counts[f] ?? 0
            const isActive = active === f
            const disabled = f !== 'All' && count === 0

            return (
              <li key={f}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => !disabled && setActive(f)}
                  className={[
                    'group relative w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-[10px] text-sm tracking-tight transition-colors duration-300',
                    'after:hidden',
                    isActive
                      ? 'bg-charcoal-900 text-white'
                      : disabled
                        ? 'text-charcoal-300 cursor-not-allowed'
                        : 'text-charcoal-600 hover:bg-charcoal-100 hover:text-charcoal-900',
                  ].join(' ')}
                  aria-pressed={isActive}
                >
                  <span className="flex items-center gap-2">
                    {/* Dot indicator — solid when active, ring otherwise */}
                    <span
                      aria-hidden="true"
                      className={[
                        'inline-block w-1.5 h-1.5 rounded-full transition-colors',
                        isActive ? 'bg-white' : 'bg-charcoal-300 group-hover:bg-charcoal-500',
                      ].join(' ')}
                    />
                    {f}
                  </span>
                  <span
                    className={[
                      'text-xs tabular-nums transition-colors',
                      isActive ? 'text-white/60' : 'text-charcoal-400',
                    ].join(' ')}
                  >
                    {count}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </aside>

      {/* ───────────────── Project grid ───────────────── */}
      <div>
        <div className="mb-8">
          <h2 className="font-display font-medium text-2xl md:text-3xl tracking-[-0.02em] uppercase text-charcoal-800">
            {active} Projects
          </h2>
        </div>

        {filtered.length === 0 ? (
          <div className="border border-dashed border-charcoal-200 rounded-2xl py-20 text-center">
            <p className="text-charcoal-500 text-sm">
              No projects in this category yet — check back soon.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-14">
            {filtered.map((project) => (
              <ProjectCard key={project.title} project={project} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
